import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  ELMA365IdentityType,
  ELMA365ProcessAvailability,
  ELMA365ProcessModel,
  ELMA365ProcessVisibility,
  ELMA365ToolsetModel,
  ELMA365VersionAction,
} from '../../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../../recoil';
import { defaultApi } from '../../../../../apis';
import IbDebounceInput from '../../../../../components/common/IbDebounceInput';
import IbPagination from '../../../../../components/common/IbPagination';
import IbTable from '../../../../../components/common/IbTable';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTextHighlighter from '../../../../../components/common/IbTextHighlighter';
import IbIcon from '../../../../../components/common/IbIcon';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import { getComponentKey } from '../utils';

import { CHECKBOX_CLASS_NAME, MAIN_CLASS_NAME, PROCESS_PAGE_SIZE, SEARCH_CLASS_NAME } from './const';

interface IProcessTableProps {
  action: ELMA365VersionAction;
  toolset?: ELMA365ToolsetModel;
  versionId?: string;
  toggledIds?: { [key: string]: boolean };
  onToggle?: (id: string, selected: boolean) => void;
}

const ProcessTable: React.FC<IProcessTableProps> = ({ action, toolset, versionId, toggledIds, onToggle }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: PROCESS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [processes, setProcesses] = useState<ELMA365ProcessModel[]>([]);

  const [loading, setLoading] = useState(true);

  const translate = getTranslateFunc('Toolkits: ELMA365: Processes:');

  const selectedKeys = new Set();
  if (toolset) {
    for (const namespace of toolset.namespaces) {
      for (const component of namespace.components) {
        if (component.identity.type === ELMA365IdentityType.Process) {
          const key = getComponentKey(namespace, component);
          selectedKeys.add(key);
        }
      }
    }
  }

  const isSelectionDisabled = (process: ELMA365ProcessModel) => {
    if (action !== ELMA365VersionAction.Appending) {
      return false;
    }

    const key = getComponentKey(process.namespace, process);
    return selectedKeys.has(key);
  };

  const isProcessSelected = (process: ELMA365ProcessModel) => {
    return toggledIds?.[process.process_id] ?? process.availability === ELMA365ProcessAvailability.Enabled;
  };

  const loadProcesses = async () => {
    if (!versionId) {
      setProcesses([]);
      return;
    }

    setLoading(true);

    try {
      const response = await defaultApi.searchELMA365ToolkitProcesses(
        (pagination.current || 1) - 1,
        PROCESS_PAGE_SIZE,
        true,
        searchText,
        undefined,
        undefined,
        [versionId],
        undefined,
        undefined,
        undefined,
        [ELMA365ProcessVisibility.Visible]
      );
      setProcesses(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadProcesses().finally();
  }, [versionId, pagination.current, pagination.pageSize, searchText]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [versionId, searchText]);

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onProcessToggle = (process: ELMA365ProcessModel) => {
    if (isSelectionDisabled(process)) {
      return;
    }
    onToggle?.(process.process_id, !isProcessSelected(process));
  };

  const renderSearch = () => {
    return (
      <div className={SEARCH_CLASS_NAME}>
        <IbDebounceInput
          allowClear
          disabled={!versionId}
          placeholder={translate('Search: Placeholder')}
          prefix={<IbIcon iconName="search" />}
          onDebouncedChange={onSearchTextChange}
        />
      </div>
    );
  };

  const renderCheckbox = (process: ELMA365ProcessModel) => {
    const onCheckboxChange = () => onProcessToggle(process);

    return (
      <div className={CHECKBOX_CLASS_NAME}>
        <IbCheckbox
          disabled={isSelectionDisabled(process)}
          value={isProcessSelected(process)}
          onChange={onCheckboxChange}
        />
        <IbTypography.Paragraph ellipsis type="secondary">
          <IbTextHighlighter highlightedText={searchText} text={process.identity.name} />
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<ELMA365ProcessModel>[] = [
      {
        title: translate('Table: Name'),
        width: '36%',
        ellipsis: true,
        render: (_, process) => renderCheckbox(process),
      },
      {
        title: translate('Table: Code'),
        width: '32%',
        ellipsis: true,
        render: (_, process) => (
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={`${process.location} / ${process.identity.code}`} />
          </IbTypography.Paragraph>
        ),
      },
      {
        title: translate('Table: Namespace'),
        width: '32%',
        ellipsis: true,
        render: (_, process) => (
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={process.namespace.identity.name} />
          </IbTypography.Paragraph>
        ),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={processes}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={!versionId || loading}
        rowKey={(process: ELMA365ProcessModel) => process.process_id}
        size="middle"
        onRowClick={onProcessToggle}
      />
    );
  };

  const renderPagination = () => {
    if ((pagination.total ?? 0) <= PROCESS_PAGE_SIZE) {
      return null;
    }

    return (
      <IbPagination
        current={pagination.current}
        defaultCurrent={1}
        defaultPageSize={PROCESS_PAGE_SIZE}
        pageSize={PROCESS_PAGE_SIZE}
        showSizeChanger={false}
        total={pagination.total}
        onChange={onPaginationChange}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderSearch()}
      {renderTable()}
      {renderPagination()}
    </div>
  );
};

export default ProcessTable;
